import { useInfiniteQuery, useMutation } from '@tanstack/react-query';
import { api } from '@/modules/common/api/index.ts';
import { useGetCurrentBusinessId } from '@/modules/common/hooks/useGetCurrentBusinessId.ts';
import { queryClient } from '@/modules/common/query.ts';
import { CURRENT_USER_TASKS_ACTIVITY_LOGS_QUERY_KEY } from '@/modules/admin-dashboard/queries/tasks.ts';
import {
  CreateClientRequest,
  GetClientsListResponse,
  GetClientsListV1ClientsGetParams,
  UpdateClientRequest,
} from 'compliance-api';

export const CURRENT_USER_CLIENTS_LIST_QUERY_KEY = 'vestra-compliance/common-clients-list';

export const useClientsListQuery = (
  payload: GetClientsListV1ClientsGetParams,
  disabled = false,
  options?: { cacheKey?: string },
) => {
  const businessId = useGetCurrentBusinessId();
  return useInfiniteQuery<GetClientsListResponse>({
    queryKey: [
      CURRENT_USER_CLIENTS_LIST_QUERY_KEY,
      JSON.stringify(payload),
      businessId,
      options?.cacheKey,
    ],
    staleTime: 1000 * 60 * 60 * 3,
    queryFn: async ({ pageParam = 1 }) => {
      const response = await api.v1.getClientsListV1ClientsGet({
        ...payload,
        page:
          typeof pageParam === 'number' && pageParam ? pageParam : pageParam?.page || payload.page,
        filter_by_name: pageParam?.search || payload.filter_by_name,
      });
      return response.data;
    },
    keepPreviousData: true,
    retry: 2,
    getNextPageParam: ({ total, page, page_size }) => {
      const totalPage = Math.ceil(total / page_size);
      const hasMore = totalPage > page;
      if (hasMore) {
        return page + 1;
      }
    },
    enabled: !disabled,
  });
};

export const useCreateClientMutation = () => {
  return useMutation({
    mutationFn: async (payload: CreateClientRequest) => {
      const response = await api.v1.createClientV1ClientsPost(payload);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries([CURRENT_USER_CLIENTS_LIST_QUERY_KEY]);
      queryClient.invalidateQueries([CURRENT_USER_TASKS_ACTIVITY_LOGS_QUERY_KEY]);
    },
  });
};

export const useDeleteClientMutation = () => {
  return useMutation({
    mutationFn: async (id: number) => {
      const response = await api.v1.deleteClientV1ClientsIdDelete({ id });
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries([CURRENT_USER_CLIENTS_LIST_QUERY_KEY]);
      queryClient.invalidateQueries([CURRENT_USER_TASKS_ACTIVITY_LOGS_QUERY_KEY]);
    },
  });
};

export const useUpdateClientMutation = () => {
  return useMutation({
    mutationFn: async (payload: {
      id: number;
      data: { name?: string; email?: string; phone_number?: string };
    }) => {
      const { id, data } = payload;
      const updateRequest: UpdateClientRequest = {
        client_id: id,
        data,
      };
      const response = await api.v1.updateClientV1ClientsIdPut({ id }, updateRequest);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries([CURRENT_USER_CLIENTS_LIST_QUERY_KEY]);
      queryClient.invalidateQueries([CURRENT_USER_TASKS_ACTIVITY_LOGS_QUERY_KEY]);
    },
  });
};
